import { ref } from 'vue';

/**
 * App Store / Google Play links for the landing site, carrying install attribution.
 *
 * When a visitor arrives from an ad, the inbound campaign parameters are folded into the
 * store links, so App Store Connect and Play Console can report installs per ad creative
 * rather than just "came from the website".
 *
 * `placement` identifies where on the page the link was rendered ('hero', 'navbar', ...).
 */

const APP_STORE_BASE =
  'https://apps.apple.com/il/app/timeit-%D7%A0%D7%99%D7%94%D7%95%D7%9C-%D7%A2%D7%A1%D7%A7-%D7%91%D7%99%D7%95%D7%98%D7%99/id6466124526';

const GOOGLE_PLAY_BASE = 'https://play.google.com/store/apps/details?id=com.timeit.app';

/**
 * Apple's campaign "provider token" — the App Store Connect account identifier. It is not a
 * secret (it is visible in every campaign link), so it is checked in as the default and can
 * be overridden per environment with VITE_APP_STORE_PROVIDER_TOKEN.
 */
const APP_STORE_PROVIDER_TOKEN = import.meta.env.VITE_APP_STORE_PROVIDER_TOKEN || '126560419';

/** Apple truncates campaign tokens; App Store Connect caps the field at 30 characters. */
const APP_STORE_CAMPAIGN_MAX_LENGTH = 30;

interface InboundCampaign {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
}

/**
 * Populated on mount rather than at module load: the landing page is prerendered, and
 * reading the URL during the first client render would not match the server-rendered HTML.
 * Until it fills in, the links carry the placement-only fallback, which is still valid.
 */
const inboundCampaign = ref<InboundCampaign>({});

export const captureInboundCampaign = (): void => {
  if (import.meta.env.SSR || typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const read = (key: string) => params.get(key) || undefined;

  inboundCampaign.value = {
    source: read('utm_source'),
    medium: read('utm_medium'),
    campaign: read('utm_campaign'),
    content: read('utm_content'),
    term: read('utm_term'),
  };
};

/** Apple accepts a limited character set for campaign tokens. */
const sanitizeCampaignToken = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '');

/**
 * The campaign token identifies the ad creative when we know it, since that is the axis
 * worth comparing. utm_campaign is deliberately not used: it holds the numeric Meta
 * campaign id, which is identical across creatives and would consume most of the 30
 * characters Apple allows.
 */
const appStoreCampaignToken = (placement: string): string => {
  const source = inboundCampaign.value.source;
  const token = `lp_${sanitizeCampaignToken(source || placement)}`;
  return token.slice(0, APP_STORE_CAMPAIGN_MAX_LENGTH);
};

export const appStoreUrl = (placement: string): string => {
  const params = new URLSearchParams();
  if (APP_STORE_PROVIDER_TOKEN) params.set('pt', APP_STORE_PROVIDER_TOKEN);
  params.set('ct', appStoreCampaignToken(placement));
  params.set('mt', '8');
  return `${APP_STORE_BASE}?${params.toString()}`;
};

export const googlePlayUrl = (placement: string): string => {
  const inbound = inboundCampaign.value;

  // Play passes `referrer` verbatim to Play Console's acquisition reports (and to the
  // Install Referrer API, if the app ever reads it). There is no length limit worth
  // worrying about here, so every inbound parameter is preserved.
  const referrer = new URLSearchParams({
    utm_source: inbound.source || 'landing',
    utm_medium: inbound.medium || 'web',
    utm_campaign: inbound.campaign || `landing_${placement}`,
    // Placement travels in utm_content so it survives alongside the ad's own campaign.
    utm_content: placement,
    ...(inbound.term ? { utm_term: inbound.term } : {}),
  }).toString();

  return `${GOOGLE_PLAY_BASE}&referrer=${encodeURIComponent(referrer)}`;
};
